import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { HelpCircle, Loader, ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import ReportPropertyErrors from './ReportPropertyErrors';

interface TaxAppealGuidanceProps {
  county: string;
}

interface Bullet {
  title?: string;
  items?: string[];
}

interface Subsection {
  title: string;
  content?: string;
  bullets?: string[];
}

interface Section {
  title: string;
  content?: string;
  bullets?: (string | Bullet)[];
  footer?: string;
  subsections?: Subsection[];
}

interface CountyGuidance {
  county: string;
  asOfDate: string;
  sections: Section[];
  appealWebsite: string;
  officeContact: string;
  additionalNotes: string;
}

const TaxAppealGuidance: React.FC<TaxAppealGuidanceProps> = ({ county }) => {
  const [isLoading, setIsLoading] = useState(false);

  // County-specific guidance - in a real app, this would come from an API
  // NOTE TO DEVELOPERS: Use ChatGPT or other LLM to generate relevant county-specific
  // data for each supported county, following the structure below
  const countyGuidance: CountyGuidance = {
    county: 'Boulder County, CO',
    asOfDate: 'May 15, 2025',
    sections: [
      {
        title: 'Property Tax Payment Deadlines',
        content:
          'In Colorado, property taxes are paid in arrears. For the 2024 tax year (payable in 2025), Boulder County property owners have the following payment options:',
        bullets: [
          {
            title: 'Two Equal Installments:',
            items: [
              'First Half: Due by February 28, 2025',
              'Second Half: Due by June 16, 2025',
            ],
          },
          {
            title: 'Full Payment:',
            items: ['Due by April 30, 2025'],
          },
        ],
        footer:
          'Payments can be made online, by mail, or in person. Late payments may incur additional fees and interest.',
      },
      {
        title: 'Assessment Release',
        content:
          "Property assessments in Colorado are conducted every odd-numbered year. For the 2025 assessment cycle, the Boulder County Assessor's Office mailed Notices of Valuation (NOV) to property owners on May 1, 2025.",
        footer:
          'The assessed values are based on market data from a 24-month period spanning July 1, 2022, through June 30, 2024. Sales within this period are time-adjusted to reflect market conditions as of June 30, 2024.',
      },
      {
        title: 'Protest and Appeal Process',
        subsections: [
          {
            title: 'Filing an Appeal:',
            content:
              "If you believe your property's assessed value is incorrect, you have the right to appeal. The appeal period for real property valuations in 2025 is from May 1 to June 9, 2025.",
          },
          {
            title: 'Accepted Evidence:',
            content:
              'When appealing, you may submit evidence such as comparable property sales that occurred between July 1, 2022, and June 30, 2024. These sales are used to determine market value and must be time-adjusted to the appraisal date of June 30, 2024.',
          },
          {
            title: 'How to File:',
            content: 'Appeals can be submitted through various methods:',
            bullets: [
              'Online: Boulder County Assessor Appeals',
              'Email: assessor@bouldercounty.gov',
              'Fax: 303-441-4996',
              'Mail:',
              '    Boulder County Assessor',
              '    PO Box 471',
              '    Boulder, CO 80306',
              "In Person: At the Assessor's Office or designated drop boxes.",
            ],
          },
          {
            title: 'Post-Appeal Process:',
            content:
              "The Assessor's Office will mail a Notice of Determination by August 15, 2025. If you disagree with this determination, you can file an appeal with the Board of Equalization by September 15, 2025.",
          },
        ],
      },
      {
        title: 'Assessor Contact Information',
        bullets: [
          "Boulder County Assessor's Office",
          'Phone: 303-441-3530',
          'Email: assessor@bouldercounty.gov',
          'Mailing Address:',
          '    PO Box 471',
          '    Boulder, CO 80306-0471',
          'Physical Address:',
          '    1325 Pearl Street, 2nd Floor',
          '    Boulder, CO 80302',
          'Website: Boulder County Assessor',
          'Appeals Page: Real Property Valuation Appeals',
        ],
      },
      {
        title: 'Verification Statement',
        content:
          "All information provided is based on the most current and publicly available data from official Boulder County government sources as of May 15, 2025. For the most accurate and personalized information, homeowners are encouraged to contact the Boulder County Assessor's Office directly.",
      },
    ],
    appealWebsite: 'https://www.bouldercounty.gov/property-assessment-appeals/',
    officeContact: '(303) 441-3530',
    additionalNotes:
      'Boulder County typically processes appeals within 4-6 weeks.',
  };

  const handleOpenSheet = () => {
    setIsLoading(true);

    // Simulate loading time - in a real app this would be an API call
    setTimeout(() => {
      setIsLoading(false);
    }, 5000);
  };

  const handleEmailRequest = () => {
    // DEVELOPER NOTE: Check user login status here
    // If user is logged in, send the email
    // If not, redirect to login or signup page

    // For demo purposes, we'll just show an alert
    alert(
      'Please log in or create an account to receive tax appeal guidelines via email.',
    );

    // In a real implementation, you would:
    // 1. Check if user is authenticated
    // 2. If yes, send email and show confirmation
    // 3. If no, redirect to login/signup page with return URL to come back to this page
  };

  return (
    <div className="mt-8">
      <div className="bg-muted border-border p-4 rounded-lg mb-4 border">
        <div className="flex items-start">
          <div className="h-8 w-8 bg-primary/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
            <HelpCircle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-tax-deepBlue mb-1">
              Important Tip
            </h3>
            <p className="text-sm text-tax-neutralGray mb-2">
              Review the property details carefully. County records often
              contain errors that could be impacting your tax assessment.
            </p>
          </div>
        </div>
        <ReportPropertyErrors />
      </div>
    </div>
  );
};

export default TaxAppealGuidance;
